<template>
  <div class="container mt-4">
    <div class="customCard">
      <h1 style="font-size: 2rem; text-align: center; padding-bottom: 1rem">
        List of Schedules
      </h1>
    </div>

    <div v-if="loading" class="text-center">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    <div v-else-if="error" class="text-center text-danger">
      <p>Error loading data.</p>
    </div>
    <div v-else>
      <button class="btn btn-primary mb-4" @click="showAddModal = true">
        Add Schedule
      </button>
      <table class="table table-striped">
        <thead>
          <tr>
            <th scope="col">Time</th>
            <th scope="col">Type</th>
            <th scope="col">Detail</th>
            <th scope="col">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="schedule in paginatedSchedules" :key="schedule.id">
            <td>
              <a href="#" @click.prevent="viewEntry(schedule)"
                >{{ formatTimeHHMM(schedule.attributes.time) }}
              </a>
            </td>
            <td>{{ getScheduleTypeText(schedule.attributes.type) }}</td>
            <td v-if="isDaily(schedule.attributes.type)">
              {{ getDay("mo", schedule.attributes.mo) }}
              {{ getDay("tu", schedule.attributes.tu) }}
              {{ getDay("we", schedule.attributes.we) }}
              {{ getDay("th", schedule.attributes.th) }}
              {{ getDay("fr", schedule.attributes.fr) }}
              {{ getDay("sa", schedule.attributes.sa) }}
              {{ getDay("su", schedule.attributes.su) }}
            </td>
            <td v-else-if="isDWeek(schedule.attributes.type)">
              {{ getDay("mo", schedule.attributes.mo) }}
              {{ getDay("tu", schedule.attributes.tu) }}
              {{ getDay("we", schedule.attributes.we) }}
              {{ getDay("th", schedule.attributes.th) }}
              {{ getDay("fr", schedule.attributes.fr) }}
              {{ getDay("sa", schedule.attributes.sa) }}
              {{ getDay("su", schedule.attributes.su) }}
            </td>
            <td><a href="#" @click.prevent="editEntry(schedule)">Edit</a></td>
          </tr>
        </tbody>
      </table>
      <div class="d-flex justify-content-between align-items-center">
        <p>
          Showing {{ startIndex + 1 }} to {{ endIndex }} of
          {{ result.residentschedules.data.length }} entries
        </p>
        <ul class="pagination">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <a
              class="page-link"
              href="#"
              @click.prevent="changePage(currentPage - 1)"
              >Prev</a
            >
          </li>
          <li
            class="page-item"
            v-for="page in totalPages"
            :key="page"
            :class="{ active: currentPage === page }"
          >
            <a class="page-link" href="#" @click.prevent="changePage(page)">{{
              page
            }}</a>
          </li>
          <li
            class="page-item"
            :class="{ disabled: currentPage === totalPages }"
          >
            <a
              class="page-link"
              href="#"
              @click.prevent="changePage(currentPage + 1)"
              >Next</a
            >
          </li>
        </ul>
      </div>
    </div>

    <!-- Edit Modal -->
    <b-modal
      size="xl"
      v-model="showEditModal"
      hide-footer
      title="Edit Schedule"
    >
      <form v-if="currentSchedule" @submit.prevent="updateEntry">
        <div class="row">
          <div class="col-md-4">
            <div class="mb-3">
              <label for="time" class="form-label">Time</label>
              <input
                type="time"
                class="form-control"
                id="time"
                v-model="currentSchedule.attributes.time"
                required
              />
            </div>
          </div>
          <div class="col-md-4">
            <div class="mb-3">
              <label for="type" class="form-label">Type</label>
              <select
                class="form-select"
                id="type"
                v-model="currentSchedule.attributes.type"
                required
              >
                <option
                  v-for="option in scheduleTypes"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.text }}
                </option>
              </select>
            </div>
          </div>
          <div class="col-md-4">
            <div class="mb-3">
              <label for="detail" class="form-label">Detail</label>
              <div v-if="isDaily(currentSchedule.attributes.type)">
                <div class="d-flex flex-wrap">
                  <div
                    class="form-check me-3"
                    v-for="(day, index) in days"
                    :key="index"
                  >
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="'checkbox' + index"
                      v-model="selectedDaysEdit"
                      :value="day"
                      :disabled="isDaily(currentSchedule.attributes.type)"
                    />
                    <label class="form-check-label" :for="'checkbox' + index">
                      {{ day }}
                    </label>
                  </div>
                </div>
              </div>
              <div v-if="isDWeek(currentSchedule.attributes.type)">
                <div class="d-flex flex-wrap">
                  <div
                    class="form-check me-3"
                    v-for="(day, index) in days"
                    :key="index"
                  >
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="'checkbox' + index"
                      v-model="selectedDaysEdit"
                      :value="day"
                    />
                    <label class="form-check-label" :for="'checkbox' + index">
                      {{ day }}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="d-grid">
          <button type="submit" class="btn btn-primary">Save changes</button>
        </div>
      </form>
      <div v-if="updateError" class="alert alert-danger mt-3">
        {{ updateError }}
      </div>
    </b-modal>

    <!-- View Modal -->
    <b-modal
      size="xl"
      v-model="showViewModal"
      hide-footer
      title="View Schedule"
    >
      <form v-if="currentSchedule" @submit.prevent="updateEntry">
        <div class="row">
          <div class="col-md-4">
            <div class="mb-3">
              <label for="time" class="form-label">Time</label>
              <input
                type="time"
                class="form-control"
                id="time"
                v-model="currentSchedule.attributes.time"
                required
                :disabled="true"
              />
            </div>
          </div>
          <div class="col-md-4">
            <div class="mb-3">
              <label for="type" class="form-label">Type</label>
              <select
                class="form-select"
                id="type"
                v-model="currentSchedule.attributes.type"
                required
                :disabled="true"
              >
                <option
                  v-for="option in scheduleTypes"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.text }}
                </option>
              </select>
            </div>
          </div>
          <div class="col-md-4">
            <div class="mb-3">
              <label for="detail" class="form-label">Detail</label>
              <div v-if="isDaily(currentSchedule.attributes.type)">
                <div class="d-flex flex-wrap">
                  <div
                    class="form-check me-3"
                    v-for="(day, index) in days"
                    :key="index"
                  >
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="'checkbox' + index"
                      v-model="selectedDaysEdit"
                      :value="day"
                      :disabled="true"
                    />
                    <label class="form-check-label" :for="'checkbox' + index">
                      {{ day }}
                    </label>
                  </div>
                </div>
              </div>
              <div v-if="isDWeek(currentSchedule.attributes.type)">
                <div class="d-flex flex-wrap">
                  <div
                    class="form-check me-3"
                    v-for="(day, index) in days"
                    :key="index"
                  >
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="'checkbox' + index"
                      v-model="selectedDaysEdit"
                      :value="day"
                      :disabled="true"
                    />
                    <label class="form-check-label" :for="'checkbox' + index">
                      {{ day }}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="d-grid">
          <!-- <button type="submit" class="btn btn-primary">Save changes</button> -->
        </div>
      </form>
      <div v-if="updateError" class="alert alert-danger mt-3">
        {{ updateError }}
      </div>
    </b-modal>

    <!-- Add Modal -->
    <b-modal size="xl" v-model="showAddModal" hide-footer title="Add Schedule">
      <form @submit.prevent="addEntry">
        <div class="row">
          <div class="col-md-4">
            <div class="mb-3">
              <label for="time" class="form-label">Time</label>
              <input
                type="time"
                class="form-control"
                id="time"
                v-model="newSchedule.time"
                required
              />
            </div>
          </div>
          <div class="col-md-4">
            <div class="mb-3">
              <label for="type" class="form-label">Type</label>
              <select
                class="form-select"
                id="type"
                v-model="newSchedule.type"
                required
              >
                <option
                  v-for="option in scheduleTypes"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.text }}
                </option>
              </select>
            </div>
          </div>
          <div class="col-md-4">
            <div class="mb-3">
              <label for="detail" class="form-label">Detail</label>
              <div v-if="isDaily(newSchedule.type)">
                <div class="d-flex flex-wrap">
                  <div
                    class="form-check me-3"
                    v-for="(day, index) in days"
                    :key="index"
                  >
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="'checkbox' + index"
                      v-model="selectedDays"
                      :value="day"
                      :disabled="isDaily(newSchedule.type)"
                    />
                    <label class="form-check-label" :for="'checkbox' + index">
                      {{ day }}
                    </label>
                  </div>
                </div>
              </div>
              <div v-if="isDWeek(newSchedule.type)">
                <div class="d-flex flex-wrap">
                  <div
                    class="form-check me-3"
                    v-for="(day, index) in days"
                    :key="index"
                  >
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="'checkbox' + index"
                      v-model="selectedDays"
                      :value="day"
                    />
                    <label class="form-check-label" :for="'checkbox' + index">
                      {{ day }}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="d-grid">
          <button type="submit" class="btn btn-primary">Add Schedule</button>
        </div>
      </form>
      <div v-if="addError" class="alert alert-danger mt-3">
        {{ addError }}
      </div>
    </b-modal>
  </div>
</template>
  
  <script>
import { useQuery, useMutation } from "@vue/apollo-composable";
import {
  GET_SCHEDULES,
  UPDATE_SCHEDULE,
  CREATE_SCHEDULE,
} from "../hooks/queries";
import { ref, computed, watch } from "vue";
import {
  scheduleTypes,
  formatTimeHHMMSSMMM,
  formatTimeHHMM,
} from "../components/values";

export default {
  name: "ScheduleComp",

  setup() {
    const { result, loading, error, refetch } = useQuery(GET_SCHEDULES);
    const { mutate: updateResidentschedule } = useMutation(UPDATE_SCHEDULE);
    const { mutate: createSchedule } = useMutation(CREATE_SCHEDULE);
    const currentSchedule = ref(null);
    const updateError = ref(null);
    const addError = ref(null);
    const showEditModal = ref(false);
    const showAddModal = ref(false);
    const showViewModal = ref(false);

    const days = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
    const selectedDays = ref([]);
    const selectedDaysEdit = ref([]);

    const newSchedule = ref({
      time: "",
      type: "",
      publishedAt: new Date().toISOString(),
    });

    const isDaily = (value) => {
      const option = scheduleTypes.find((option) => option.value === value);
      return option ? option.showDaily : false;
    };

    const showDaily = computed(() => {
      const selectedOption = scheduleTypes.find(
        (option) => option.value === newSchedule.value.type
      );
      return selectedOption ? selectedOption.showDaily : false;
    });

    const isDWeek = (value) => {
      const option = scheduleTypes.find((option) => option.value === value);
      return option ? option.showDWeek : false;
    };

    const showDWeek = computed(() => {
      const selectedOption = scheduleTypes.find(
        (option) => option.value === newSchedule.value.type
      );
      return selectedOption ? selectedOption.showDWeek : false;
    });

    const addEntry = async () => {
      addError.value = null;
      try {
        await createSchedule({
          data: {
            type: newSchedule.value.type,
            time: formatTimeHHMMSSMMM(newSchedule.value.time),
            mo: selectedDays.value.includes("Mo"),
            tu: selectedDays.value.includes("Tu"),
            we: selectedDays.value.includes("We"),
            th: selectedDays.value.includes("Th"),
            fr: selectedDays.value.includes("Fr"),
            sa: selectedDays.value.includes("Sa"),
            su: selectedDays.value.includes("Su"),
            publishedAt: new Date().toISOString(),
          },
        });
        showAddModal.value = false;
        newSchedule.value = {
          time: "",
          type: "",
        };
        await refetch(); // Refetch the list of schedules after adding a new one
      } catch (e) {
        //console.error("Error adding entry:", e);
        addError.value = `Failed to add schedule. Please try again.}`;
      }
    };
    const editEntry = (schedule) => {
      //currentSchedule.value = { ...schedule }; // Copy schedule to avoid directly modifying the original data
      currentSchedule.value = JSON.parse(JSON.stringify(schedule)); // Create a deep copy of the document object
      //console.log(schedule);
      selectedDaysEdit.value = [];

      if (schedule.attributes.mo) selectedDaysEdit.value.push("Mo");
      if (schedule.attributes.tu) selectedDaysEdit.value.push("Tu");
      if (schedule.attributes.we) selectedDaysEdit.value.push("We");
      if (schedule.attributes.th) selectedDaysEdit.value.push("Th");
      if (schedule.attributes.fr) selectedDaysEdit.value.push("Fr");
      if (schedule.attributes.sa) selectedDaysEdit.value.push("Sa");
      if (schedule.attributes.su) selectedDaysEdit.value.push("Su");

      showEditModal.value = true;
    };

    const updateEntry = async () => {
      updateError.value = null; // Reset the error message
      try {
        await updateResidentschedule({
          id: currentSchedule.value.id,
          data: {
            type: currentSchedule.value.attributes.type,
            time: formatTimeHHMMSSMMM(currentSchedule.value.attributes.time),
            mo: selectedDaysEdit.value.includes("Mo"),
            tu: selectedDaysEdit.value.includes("Tu"),
            we: selectedDaysEdit.value.includes("We"),
            th: selectedDaysEdit.value.includes("Th"),
            fr: selectedDaysEdit.value.includes("Fr"),
            sa: selectedDaysEdit.value.includes("Sa"),
            su: selectedDaysEdit.value.includes("Su"),
          },
        });
        //console.log("Updated entry:", currentSchedule.value);
        showEditModal.value = false;
      } catch (error) {
        //console.error("Error updating entry:", error);
        updateError.value = "Failed to update Schedule. Please try again.";
      }
    };

    const viewEntry = (schedule) => {
      currentSchedule.value = JSON.parse(JSON.stringify(schedule)); // Create a deep copy of the ADL object
      showViewModal.value = true;
    };

    const getScheduleTypeText = (value) => {
      const option = scheduleTypes.find((option) => option.value === value);
      return option ? option.text : "";
    };

    const getDay = (dd, value) => {
      if (!value) return "";

      switch (dd.toLowerCase()) {
        case "mo":
          return "| Mo |";
        case "tu":
          return "Tu |";
        case "we":
          return "We |";
        case "th":
          return "Th |";
        case "fr":
          return "Fr |";
        case "sa":
          return "Sa |";
        case "su":
          return "Su |";
        default:
          return "";
      }
    };

    // Watcher for newSchedule type to automatically select all days if type is daily
    watch(
      () => newSchedule.value.type,
      (newValue) => {
        if (isDaily(newValue)) {
          selectedDays.value = days.slice(); // Select all days
        } else {
          selectedDays.value = []; // Clear selection if not daily
        }
      }
    );

    // Watcher for currentSchedule type to automatically select all days if type is daily
    watch(
      () => currentSchedule.value?.attributes?.type,
      (newValue) => {
        if (isDaily(newValue)) {
          selectedDaysEdit.value = days.slice(); // Select all days
          console.log(selectedDaysEdit.value);
        } else if (currentSchedule.value) {
          selectedDaysEdit.value = [];
          console.log(selectedDaysEdit.value);
          /**
          if (currentSchedule.value.attributes.mo) selectedDaysEdit.value.push("Mo");
          if (currentSchedule.value.attributes.tu) selectedDaysEdit.value.push("Tu");
          if (currentSchedule.value.attributes.we) selectedDaysEdit.value.push("We");
          if (currentSchedule.value.attributes.th) selectedDaysEdit.value.push("Th");
          if (currentSchedule.value.attributes.fr) selectedDaysEdit.value.push("Fr");
          if (currentSchedule.value.attributes.sa) selectedDaysEdit.value.push("Sa");
          if (currentSchedule.value.attributes.su) selectedDaysEdit.value.push("Su");
           */
        }
      }
    );

    return {
      result,
      loading,
      error,
      formatTimeHHMMSSMMM,
      formatTimeHHMM,
      showEditModal,
      showAddModal,
      showViewModal,
      currentSchedule,
      newSchedule,
      editEntry,
      viewEntry,
      updateEntry,
      addEntry,
      updateError,
      addError,
      scheduleTypes,
      getScheduleTypeText,
      days,
      selectedDays,
      selectedDaysEdit,
      isDaily,
      showDaily,
      isDWeek,
      showDWeek,
      getDay,
    };
  },
  data() {
    return {
      currentPage: 1,
      itemsPerPage: 10,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(
        this.result.residentschedules.data.length / this.itemsPerPage
      );
    },
    paginatedSchedules() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.result.residentschedules.data.slice(
        start,
        start + this.itemsPerPage
      );
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    endIndex() {
      return Math.min(
        this.startIndex + this.itemsPerPage,
        this.result.residentschedules.data.length
      );
    },
  },
  methods: {
    changePage(page) {
      if (page > 0 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
  },
};
</script>
  
  <style>
.container {
  max-width: 80%;
}

.d-grid {
  background-color: #e8e8e8;
  margin-bottom: 20px;
}

.text-center {
  text-align: center;
}
.spinner-border {
  margin-top: 20px;
}
.pagination {
  margin: 0;
}
</style>
  
